import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Modal } from "semantic-ui-react";

import { useCan, useSelectedLocation } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ROLES, USER_STATUSES } from "models";
import { PASSWORD_STRENGTH_THRESHOLD } from "modules/Login/components";
import "modules/UsersDashboard/components/CreateUserModal/CreateUserModal.scss";
import { EditUserActions } from "modules/UsersDashboard/components/EditUserModal/EditUserActions";
import { EditUserForm } from "modules/UsersDashboard/components/EditUserModal/EditUserForm";
import { useAssignedAppointmentsCountQuery, useDevicesQuery, useEditUserMutation, useMetricsQuery } from "modules/UsersDashboard/components/EditUserModal/hooks";
import { UserFormHeaderCheckboxes } from "modules/UsersDashboard/components/UserForm/UserFormHeaderCheckboxes";
import { useUserDetailsQuery } from "modules/UsersDashboard/hooks";

import type { CreateEditUserForm } from "modules/UsersDashboard/components";

type EditUserModalProps = {
  userId: number | null;
  isDpoUser: boolean;
  onClose: () => void;
};

const EDIT_USER_FORM_ID = "edit-user-form";
const FormHelper = createFormHelper<CreateEditUserForm>();

export const EditUserModal = ({ userId, onClose, isDpoUser }: EditUserModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const { user } = usePermanentRealTimeUser();
  const canUpdateUsers = useCan("update", "users");
  const { selectedLocation } = useSelectedLocation();
  const { data: details, refetch: refetchUserDetails, isLoading: isUserDetailsLoading } = useUserDetailsQuery(userId);
  const { mutateAsync: updateUserMutation } = useEditUserMutation(userId);
  const { refetch: refetchDevices } = useDevicesQuery(userId);
  const { refetch: refetchMetrics } = useMetricsQuery(userId);
  const { refetch: refetchAssignedCount } = useAssignedAppointmentsCountQuery({
    is_dayplanner_enabled: selectedLocation?.is_dayplanner_enabled,
    user_id: userId,
    dealer_location_id: details?.dealer_location_id,
    isMechanicRole: details?.role_id && [ROLES.ChiefMechanic, ROLES.Mechanic].includes(details.role_id)
  });

  const currentUserRole = user?.role_id;
  const selectedUserRole = details?.role_id;
  const isCurrentRoleAndSelectedRoleDealerAdmin = selectedUserRole === ROLES.DealerAdmin && currentUserRole === selectedUserRole;

  const canEditEntireForm = currentUserRole ? [ROLES.SuperAdmin, ROLES.SupportAdmin, ROLES.ClaireAdmin].includes(currentUserRole) : false;
  const canEditDealerAdminFields = canUpdateUsers && !canEditEntireForm;
  const canEditAll = canEditEntireForm || isCurrentRoleAndSelectedRoleDealerAdmin;

  const formMethods = FormHelper.useForm({
    values: {
      dealers_ids: details?.dealers_ids ?? null,
      location_ids: details?.location_ids ?? null,
      brand_group_ids: details?.brand_groups?.map(brandGroup => brandGroup.id) ?? [],
      confirmPassword: "",
      dms_nr: details?.dms_nr ?? null,
      dms_location_source: details?.dms_location_source ?? "",
      external_user_id: details?.external_user_id ?? "",
      email: details?.email ?? "",
      first_name: details?.first_name ?? "",
      is_combi_user: Boolean(details?.is_combi_user),
      is_counter_tablet_user: Boolean(details?.is_counter_tablet_user),
      csv_download: Boolean(details?.csv_download),
      is_keylocker_allowed: Boolean(details?.is_keylocker_allowed),
      multi_device_login_enabled: Boolean(details?.multi_device_login_enabled),
      tyre_ordering_enabled: Boolean(details?.tyre_ordering_enabled),
      last_name: details?.last_name ?? "",
      dealer_location_id: details?.dealer_location_id ?? null,
      password: "",
      phone: details?.phone ?? "",
      role_id: details?.role_id ?? null,
      username: details?.username ?? "",
      isActive: details?.status === USER_STATUSES.UserActive,
      onei_planning_monteur_id: details?.onei_planning_monteur_id ?? null,
      dealer_id: details?.dealer_id ?? null,
      planit_planning_mechanic_id: details?.planit_planning_mechanic_id ?? null,
      has_leased_tablet: details?.has_leased_tablet ?? false,
      is_tablet_translation_ai_enabled: details?.is_tablet_translation_ai_enabled ?? false
    },
    resetOptions: { keepDirtyValues: true },
    mode: "all"
  });
  const { isSubmitting } = formMethods.formState;

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const handleSetPasswordStrength = (strength: number) => setPasswordStrength(strength);

  const validateUser = (values: CreateEditUserForm) => {
    let hasErrors =
      !values.first_name ||
      !values.last_name ||
      !values.email ||
      !values.username ||
      !values.role_id ||
      !values.username ||
      !values.dealer_id ||
      !values.dealer_location_id;

    if (values.password && values.password !== values.confirmPassword) hasErrors = true;
    if (values.password && passwordStrength < PASSWORD_STRENGTH_THRESHOLD) hasErrors = true;

    setHasErrors(hasErrors);
    return hasErrors;
  };

  const handleSubmit = async (values: CreateEditUserForm) => {
    if (validateUser(values)) return;

    await updateUserMutation(values);
    handleClose();
  };

  useEffect(() => {
    if (userId && userId > 0) {
      refetchUserDetails();
      refetchDevices();
      refetchMetrics();
    }
  }, [userId]);

  useEffect(() => {
    if (userId && userId > 0 && selectedLocation?.is_dayplanner_enabled && details?.dealer_location_id && [ROLES.ChiefMechanic, ROLES.Mechanic].includes(details.role_id))
      refetchAssignedCount();
  }, [userId, selectedLocation?.is_dayplanner_enabled, details?.dealer_location_id, details?.role_id]);

  return (
    <Modal open size="large" className="CreateUserModal">
      <FormHelper.FormProvider {...formMethods}>
        <Modal.Header>
          <div className="header-container">
            <div>{t("v8_edit_user", "Edit User")}</div>
            <UserFormHeaderCheckboxes disabled={!canEditAll || isUserDetailsLoading} />
          </div>
        </Modal.Header>

        <Modal.Content scrolling>
          <Form id={EDIT_USER_FORM_ID} onSubmit={formMethods.handleSubmit(handleSubmit)} loading={isSubmitting || isUserDetailsLoading} disabled={isSubmitting}>
            <EditUserForm
              userId={userId}
              hasErrors={hasErrors}
              canEditAll={canEditAll}
              canEditDealerAdminFields={canEditDealerAdminFields}
              passwordStrength={passwordStrength}
              onPasswordStrengthChange={handleSetPasswordStrength}
            />
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <EditUserActions
            formId={EDIT_USER_FORM_ID}
            userId={userId}
            isDpoUser={isDpoUser}
            isPending={isUserDetailsLoading}
            canEditEntireForm={canEditEntireForm}
            canUpdateUsers={canUpdateUsers}
            onClose={handleClose}
          />
        </Modal.Actions>
      </FormHelper.FormProvider>
    </Modal>
  );
};
